import React, { useCallback } from 'react';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Quantity from '../../Quantity';
import Modal from '~/app/common/Modal';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import { SaveDeleteMaterialResponse } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import { IssueOrServiceType } from '~/app/data/statuses/chat';

export interface ManualPartFormProps {
  open: boolean
  onClose: () => void
  onCloseAll: () => void
  type: IssueOrServiceType
}
export type CheckoutComponent = React.FC<ManualPartFormProps>

const Checkout: CheckoutComponent = ({ open, onClose, onCloseAll, type }) => {

  const basePath = (type === IssueOrServiceType.ISSUE )? 'issue' : 'service';
  const orderNo = useStoreState(state => {
    if (type === IssueOrServiceType.ISSUE) {
      return `${state.issue.data.issueNo}`;
    }
    if (type === IssueOrServiceType.SERVICE) {
      return `${state.service.data.serviceText}`;
    }
    return '';
  });
  const id = useStoreState(state => state[basePath].data.id);
  const totalQuantity = useStoreState(state => state[basePath].tempMaterials.filter(dM => !dM.deleted)?.reduce((sum, part) => sum + part.quantity, 0));
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const saveMaterialAction = useStoreActions(actions => actions[basePath].saveMaterial);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);

  const { t } = useTranslation('material');

  const handleQuantityChange = useCallback((id: string, value: number) => {
    const updatedMaterials = tempMaterials.map(material =>
      material.id === id
        ? { ...material, quantity: value }
        : material
    );
    setTempMaterialsAction(updatedMaterials);
  }, [tempMaterials, setTempMaterialsAction]);

  const handleSave = () => {
    saveMaterialAction({
      id,
      materials: tempMaterials
    }).then((res: SaveDeleteMaterialResponse) => {
      if (res.success) {
        fetchMaterialsAction(id);
        onCloseAll();
      }
    });
  }

  return (
    <Modal className={classes['checkout']} open={open} onClose={onClose} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <Icon name='arrow_back' onClick={onClose} />
          <span>{t('add')} {t('parts')}</span>
        </div>
        <div className={classes['cardHeader']}>
          <span className={classes['header']}>{t('shoppingCart')}</span>
          <span>{orderNo}</span>
          <span>{tempMaterials.length} {t('parts')} {t('selected')}</span>
        </div>
        <div className={classes['partsList']}>
          {tempMaterials.filter(dM => !dM.deleted).map((material, index) => (
            <div className={classes['partItem']} key={index}>
              <div className={classes['column']}>
                <span className={classes['partName']}>{material.article_name}</span>
                <span className={classes['partNumber']}>{t('partNo')}: {material.article_no}</span>
              </div>
              <div className={classes['column']}>
                <Quantity quantity={material.quantity} quantityChange={(value) => handleQuantityChange(material.id, value)} />
              </div>
            </div>
          ))}
        </div>
        <hr className={classes['bottomLine']} />
        <div className={classes['cardFooter']}>
          <span>{t('quantity')} {t('parts')}: <span className={classes['cardFooterQty']}> {totalQuantity} </span></span>
          <span>{t('unique')} {t('parts')}: <span className={classes['cardFooterQty']}> {tempMaterials.length} </span></span>
        </div>
        <hr className={classes['topLine']} />
        <div className={classes['partFooter']}>
          <Button className={classes['backButton']} onClick={onClose}>{t('back')}</Button>
          <Button className={classes['addButton']} onClick={handleSave}>{t('save')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default Checkout;